// ─────────────────────────────────────────────────────────────────────────────
// ContextualAIAssistant – Main Entry Point (ESG-only)
//
// Drop-in floating widget for ESG and sustainability analysis. Reads the
// dashboard's registered widgets (via window.__esgAssistant.register) and
// answers ESG questions grounded in actual on-screen data.
//
// Usage:
//   <ContextualAIAssistant
//     mode="floating"
//     position="bottom-right"
//     title="ESG Assistant"
//     aiProxyEndpoint="/api/ai"
//   />
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback } from "react";
import type { AIAssistantProps } from "./types";
import { usePageContext } from "./hooks/usePageContext";
import { useAIChat } from "./hooks/useAIChat";
import ChatWindow from "./components/ChatWindow";
import { injectGlobalStyles } from "./styles";
import { useFeatureFlag } from "./featureFlag";
// Side-effect import: instantiates window.__esgAssistant on load
import "./registry/widgetRegistry";

// ── Panel size persistence ────────────────────────────────────────────────────
// We persist only the user's preferred panel dimensions (a UI preference, not
// chat content) under a versioned key so future migrations can break cleanly.

const SIZE_STORAGE_KEY = "esgAssistant.panelSize.v1";
const DEFAULT_PANEL_SIZE = { width: 440, height: 680 };
const MIN_PANEL = { width: 360, height: 480 };

function readPersistedSize(): { width: number; height: number } {
	if (typeof window === "undefined") return DEFAULT_PANEL_SIZE;
	try {
		const raw = window.localStorage?.getItem(SIZE_STORAGE_KEY);
		if (!raw) return DEFAULT_PANEL_SIZE;
		const parsed = JSON.parse(raw);
		const w = Number(parsed?.width);
		const h = Number(parsed?.height);
		if (!Number.isFinite(w) || !Number.isFinite(h)) return DEFAULT_PANEL_SIZE;
		return {
			width: Math.max(MIN_PANEL.width, Math.min(w, 1200)),
			height: Math.max(MIN_PANEL.height, Math.min(h, 1200)),
		};
	} catch {
		return DEFAULT_PANEL_SIZE;
	}
}

function writePersistedSize(size: { width: number; height: number }): void {
	if (typeof window === "undefined") return;
	try {
		window.localStorage?.setItem(SIZE_STORAGE_KEY, JSON.stringify(size));
	} catch {
		/* ignore storage errors (private mode, quota) */
	}
}

// ── Position styles for floating widget ───────────────────────────────────────

const POSITION_STYLES: Record<string, React.CSSProperties> = {
	"bottom-right": { bottom: 24, right: 24 },
	"bottom-left": { bottom: 24, left: 24 },
	"top-right": { top: 24, right: 24 },
	"top-left": { top: 24, left: 24 },
};

const PANEL_POSITION_STYLES: Record<string, React.CSSProperties> = {
	"bottom-right": { bottom: 88, right: 24 },
	"bottom-left": { bottom: 88, left: 24 },
	"top-right": { top: 88, right: 24 },
	"top-left": { top: 88, left: 24 },
};

// ── Floating Action Button ────────────────────────────────────────────────────

interface FABProps {
	isOpen: boolean;
	hasDetectedContext: boolean;
	widgetCount: number;
	onClick: () => void;
	position: string;
}

function FloatingButton({
	isOpen,
	hasDetectedContext,
	widgetCount,
	onClick,
	position,
}: FABProps) {
	const [hovered, setHovered] = useState(false);

	return (
		<button
			onClick={onClick}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			className={!isOpen && hasDetectedContext ? "cai-fab-pulse" : undefined}
			aria-label={isOpen ? "Close ESG Assistant" : "Open ESG Assistant"}
			data-cai-widget="true"
			style={{
				position: "fixed",
				...POSITION_STYLES[position],
				width: 58,
				height: 58,
				borderRadius: "50%",
				background: isOpen
					? "linear-gradient(135deg, #6B7280, #4B5563)"
					: "linear-gradient(135deg, #482A7A 0%, #6B2A8E 50%, #B31F7E 100%)",
				border: "none",
				color: "#fff",
				fontSize: isOpen ? 20 : 24,
				cursor: "pointer",
				zIndex: 10003,
				boxShadow: hovered
					? "0 12px 32px rgba(72, 42, 122, 0.42), 0 4px 10px rgba(72, 42, 122, 0.18)"
					: "0 8px 24px rgba(72, 42, 122, 0.32), 0 2px 6px rgba(72, 42, 122, 0.14)",
				transform: hovered ? "translateY(-2px) scale(1.04)" : "scale(1)",
				transition: "transform 0.18s ease, box-shadow 0.18s ease, background 0.2s ease",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				fontFamily:
					'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
			}}
		>
			{isOpen ? "✕" : "✦"}

			{!isOpen && hasDetectedContext && (
				<span
					title={`${widgetCount} widget${widgetCount === 1 ? "" : "s"} indexed`}
					style={{
						position: "absolute",
						top: 4,
						right: 4,
						minWidth: 16,
						height: 16,
						padding: "0 4px",
						borderRadius: 999,
						background: "#34D399",
						border: "2px solid #fff",
						fontSize: 9,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						color: "#053D2A",
						fontWeight: 800,
						letterSpacing: "-0.02em",
					}}
				>
					{widgetCount > 0 ? widgetCount : "✓"}
				</span>
			)}
		</button>
	);
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function ContextualAIAssistant(props: AIAssistantProps) {
	const {
		apiKey,
		apiEndpoint,
		model,
		maxTokens,
		searchApiKey,
		searchProvider,

		mode = "floating",
		position = "bottom-right",
		title = "ESG Assistant",
		placeholder = "Ask about ESG, frameworks, or any chart on this page…",

		includeApiInterception = true,
		contextSelector = "body",
		maxContextLength = 6000,

		onMessage,
		onError,
		onContextDetected,
	} = props;

	const [isOpen, setIsOpen] = useState(mode === "embedded");
	const [panelSize, setPanelSize] = useState(() => readPersistedSize());
	const [isResizing, setIsResizing] = useState(false);

	useEffect(() => {
		injectGlobalStyles();
	}, []);

	// ── Drag-to-resize for the floating panel ─────────────────────────────────
	// Handle is the top-left corner (since the panel is anchored bottom-right).
	// Dragging up-and-left grows the panel; down-and-right shrinks it.
	const startResize = useCallback(
		(e: React.MouseEvent | React.TouchEvent) => {
			e.preventDefault();
			const startPoint =
				"touches" in e
					? { x: e.touches[0].clientX, y: e.touches[0].clientY }
					: { x: e.clientX, y: e.clientY };
			const startSize = { ...panelSize };
			setIsResizing(true);

			const onMove = (ev: MouseEvent | TouchEvent) => {
				const point =
					"touches" in ev
						? { x: ev.touches[0].clientX, y: ev.touches[0].clientY }
						: { x: ev.clientX, y: ev.clientY };
				// Inverted: drag UP → grow height; drag LEFT → grow width.
				const dx = startPoint.x - point.x;
				const dy = startPoint.y - point.y;
				const maxW = Math.min(window.innerWidth - 48, 1200);
				const maxH = Math.min(window.innerHeight - 110, 1200);
				const nextW = Math.max(MIN_PANEL.width, Math.min(maxW, startSize.width + dx));
				const nextH = Math.max(MIN_PANEL.height, Math.min(maxH, startSize.height + dy));
				setPanelSize({ width: nextW, height: nextH });
			};
			const onEnd = () => {
				setIsResizing(false);
				setPanelSize((cur) => {
					writePersistedSize(cur);
					return cur;
				});
				window.removeEventListener("mousemove", onMove);
				window.removeEventListener("mouseup", onEnd);
				window.removeEventListener("touchmove", onMove);
				window.removeEventListener("touchend", onEnd);
			};
			window.addEventListener("mousemove", onMove);
			window.addEventListener("mouseup", onEnd);
			window.addEventListener("touchmove", onMove, { passive: false });
			window.addEventListener("touchend", onEnd);
		},
		[panelSize]
	);

	// Server kill-switch — hides the assistant when ops disables it remotely.
	const featureFlag = useFeatureFlag(apiEndpoint);
	const isDisabled = !featureFlag.enabled || featureFlag.killSwitch;

	const {
		context,
		isLoading: isContextLoading,
		refreshContext,
	} = usePageContext({
		includeApiInterception,
		contextSelector,
		maxContextLength,
	});

	const aiConfig = { apiKey, apiEndpoint, model, maxTokens, searchApiKey, searchProvider };
	const {
		messages,
		isLoading,
		error,
		sendMessage,
		clearMessages,
		stopGeneration,
		setContext,
	} = useAIChat(aiConfig);

	useEffect(() => {
		if (context) {
			setContext(context);
			onContextDetected?.(context);
		}
	}, [context, setContext, onContextDetected]);

	const lastMessageCount = React.useRef(0);
	useEffect(() => {
		if (messages.length > lastMessageCount.current) {
			const newMessages = messages.slice(lastMessageCount.current);
			newMessages.forEach((m) => onMessage?.(m));
			lastMessageCount.current = messages.length;
		}
	}, [messages, onMessage]);

	useEffect(() => {
		if (error) onError?.(new Error(error));
	}, [error, onError]);

	const handleRefreshContext = useCallback(() => {
		const newCtx = refreshContext();
		if (newCtx) setContext(newCtx);
	}, [refreshContext, setContext]);

	const widgetCount = context?.widgetIndex.length ?? 0;
	const hasDetectedContext = context?.esg != null || widgetCount > 0;

	const effectivePlaceholder = context?.esg
		? "Ask about ESG metrics, climate disclosure, or any chart on this page…"
		: placeholder;

	// Kill-switch: render nothing once all hooks have run.
	if (isDisabled) {
		return null;
	}

	// ── Embedded Mode ─────────────────────────────────────────────────────────
	if (mode === "embedded") {
		return (
			<div
				data-cai-widget="true"
				style={{
					width: "100%",
					height: "100%",
					minHeight: 520,
					border: "1px solid #EEE7F6",
					borderRadius: 14,
					overflow: "hidden",
					boxShadow: "0 4px 20px rgba(72, 42, 122, 0.08)",
				}}
			>
				<ChatWindow
					messages={messages}
					isLoading={isLoading}
					error={error}
					context={context}
					isContextLoading={isContextLoading}
					onSendMessage={sendMessage}
					onStopGeneration={stopGeneration}
					onClearMessages={clearMessages}
					onRefreshContext={handleRefreshContext}
					title={title}
					placeholder={effectivePlaceholder}
				/>
			</div>
		);
	}

	// ── Floating Mode ─────────────────────────────────────────────────────────
	return (
		<>
			<FloatingButton
				isOpen={isOpen}
				hasDetectedContext={hasDetectedContext}
				widgetCount={widgetCount}
				onClick={() => setIsOpen((o) => !o)}
				position={position}
			/>

			{isOpen && (
				<div
					data-cai-widget="true"
					className={isResizing ? undefined : "cai-panel-enter"}
					style={{
						position: "fixed",
						...PANEL_POSITION_STYLES[position],
						width: panelSize.width,
						height: panelSize.height,
						maxWidth: "calc(100vw - 48px)",
						maxHeight: "calc(100vh - 110px)",
						borderRadius: 18,
						overflow: "hidden",
						boxShadow: isResizing
							? "0 28px 70px rgba(72, 42, 122, 0.42), 0 6px 18px rgba(72, 42, 122, 0.18)"
							: "0 24px 60px rgba(72, 42, 122, 0.32), 0 4px 16px rgba(72, 42, 122, 0.12)",
						zIndex: 10002,
						display: "flex",
						flexDirection: "column",
						border: "1px solid rgba(72, 42, 122, 0.10)",
						background: "#fff",
						transition: isResizing ? "none" : "box-shadow 0.18s ease",
						userSelect: isResizing ? "none" : undefined,
					}}
				>
					{/* Drag-to-resize handle (top-left corner). */}
					<div
						onMouseDown={startResize}
						onTouchStart={startResize}
						title="Drag to resize"
						aria-label="Resize chat window"
						style={{
							position: "absolute",
							top: 0,
							left: 0,
							width: 18,
							height: 18,
							cursor: "nwse-resize",
							zIndex: 10,
							display: "flex",
							alignItems: "flex-start",
							justifyContent: "flex-start",
						}}
					>
						<svg
							width="14"
							height="14"
							viewBox="0 0 14 14"
							style={{
								margin: 2,
								pointerEvents: "none",
								opacity: 0.55,
							}}
						>
							<path
								d="M2 12 L12 2 M2 8 L8 2 M2 4 L4 2"
								stroke="#fff"
								strokeWidth="1.4"
								fill="none"
								strokeLinecap="round"
							/>
						</svg>
					</div>

					<ChatWindow
						messages={messages}
						isLoading={isLoading}
						error={error}
						context={context}
						isContextLoading={isContextLoading}
						onSendMessage={sendMessage}
						onStopGeneration={stopGeneration}
						onClearMessages={clearMessages}
						onRefreshContext={handleRefreshContext}
						title={title}
						placeholder={effectivePlaceholder}
						onClose={() => setIsOpen(false)}
					/>
				</div>
			)}
		</>
	);
}

// ── Named exports for advanced usage ─────────────────────────────────────────
export { usePageContext } from "./hooks/usePageContext";
export { useAIChat } from "./hooks/useAIChat";
export { esgLookup, ESG_STANDARDS, ESG_METRICS } from "./tools/esgKnowledge";
export { webSearchTool, parallelSearchTool } from "./tools/webSearch";
export { widgetRegistry } from "./registry/widgetRegistry";
export { useWidgetRegistry, useWidgetIndex } from "./registry/useWidgetRegistry";
export type * from "./types";
export type * from "./registry/types";
